import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';

// Zola-ui
import {
  ButtonV2,
  RenderField,
  RenderTextareaField,
} from '@zola/zola-ui';

// ReduxForm
import {
  Field,
  change,
  reduxForm,
  formValueSelector,
  isDirty,
} from 'redux-form';
import * as Validations from '../../../../common/form/commonValidations';

// Actions
import { uploadPhoto } from '../../../../../actions/website/FileUploadActions';
import { updatePage, updatePageSilently } from '../../../../../actions/website/PageActions';

// Components
import { RenderHiddenField } from '../../../../common/form';
import FileInputZolaUIButton from '../../../../common/form/FileInputZolaUIButton';
import FileInput from '../../../../common/form/FileInput';

// Content
import { getCmsPageDescriptionPlaceholder } from '../CmsPageTypes';

// Sryles
import './EditPageForm.less';

const usePrevious = (value) => {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}

const EditPageForm = ({
  page,
  formValues,
  updateFormValues,
  updateFormValuesHidden,
  valid,
  handleSubmit,
  headerImgId,
  headerImgUrl,
  filePreview,
  onUpload,
  router,
  routes,
  onUpdatePageSilently,
  isFormDirty,
}) => {
  const pageType = page && page.type;
  const isPageHidden = page && page.hidden;
  const prevPageType = usePrevious(pageType);
  const [hasHeaderImage, setHasHeaderImage] = useState(false);
  const descriptionPlaceholder = getCmsPageDescriptionPlaceholder(pageType);
  const filePreviewId = filePreview && filePreview.uuid;
  const filePreviewUrl = filePreview && filePreview.cloudfront_url;
  const headerImagePreview = filePreviewUrl || headerImgUrl;
  const formValuesImageId = formValues && formValues.header_image_id;

  const onSubmitForm = (values) => {
    if(isFormDirty && valid) handleSubmit(values)
  }

  const onSubmitFormSilently = (values) => {
    if (isFormDirty && valid) onUpdatePageSilently(values)
  }

  const addImage = (image) => {
    updateFormValues(image)
    setHasHeaderImage(true)
  }

  const removeImage = () => {
    updateFormValues(null)
    setHasHeaderImage(false)
  }

  const autoSaveHeaderImage = () => {
    if (formValuesImageId !== headerImgId && valid) {
      onUpdatePageSilently({ ...page, ...formValues });
    }
  }

  const routerWillLeave = () => onSubmitForm()

  useEffect(() => {
    return () => {
      router.setRouteLeaveHook(
        routes[routes.length - 1],
        () => {}
      );
    }
  }, [])

  useEffect(() => {
    router.setRouteLeaveHook(
      routes[routes.length - 1],
      routerWillLeave
    );
  })

  useEffect(() => {
    setHasHeaderImage(!!(headerImgUrl || filePreviewId))
  }, [headerImgUrl, filePreviewId])

  useEffect(() => {
    if (prevPageType === pageType) autoSaveHeaderImage()
  }, [formValuesImageId])

  useEffect(() => {
    if (filePreviewId) addImage(filePreview)
  }, [filePreviewId])

  useEffect(() => {
    if (prevPageType === pageType) updateFormValuesHidden(isPageHidden)
  }, [isPageHidden])

  return (
    <form onSubmit={onSubmitForm} id="edit-page-form" className="edit-page-form" noValidate>
      <Field
        id="cms-page-id"
        name="id"
        component={RenderHiddenField}
      />
      <Field
        id="cms-page-hidden"
        name="hidden"
        component={RenderHiddenField}
      />
      <Field
        name="header_image_id"
        component={RenderHiddenField}
      />
      <Field
        name="header_image_url"
        component={RenderHiddenField}
      />
      <div className="title-photo-section form-group">
        <Field
          name="title"
          type="text"
          label="Page Title"
          id="cms-page-title"
          component={RenderField}
          required
          maxChars={40}
          validate={[Validations.REQUIRED, Validations.MAX_LENGTH_40]}
          onBlur={handleSubmit(onSubmitFormSilently)}
          className="page-title-field"
        />
        {!hasHeaderImage && (
          <FileInputZolaUIButton
            types="image/*"
            inputId={`header_image_${page.type}`}
            onSelect={onUpload}
            btnText="Add A Photo"
            divClass="upload-image-button"
            btnClass="v2-button secondary-button"
          />
        )}
      </div>
      {hasHeaderImage && (
        <div className="page-image header-image form-group">
          <div className="form-label">Main Image <small>(max file size 5 MB)</small></div>
          <div className="edit-image-section">
            <div className="image-preview">
              <FileInput
                filePreview={headerImagePreview}
                inputId={`header_image_${page.type}`}
                onSelect={onUpload}
              />
            </div>
            <ButtonV2 className="image-delete-button" variant="tertiary" onClick={removeImage}>Delete</ButtonV2>
          </div>
        </div>
      )}
      <Field
        name="description"
        id="cms-page-description"
        label="Description"
        placeholder={descriptionPlaceholder}
        component={RenderTextareaField}
        maxChars={1000}
        rows={4}
        validate={Validations.MAX_LENGTH_1000}
        onBlur={handleSubmit(onSubmitFormSilently)}
        className="page-description"
      />
    </form>
  );
}

const EditPageFormWithRouter = withRouter(EditPageForm);

EditPageForm.propTypes = {
  page: PropTypes.shape({}),
  handleSubmit: PropTypes.func,
  isFormDirty: PropTypes.bool,
  valid: PropTypes.bool,
  updateFormValues: PropTypes.func,
  updateFormValuesHidden: PropTypes.func,
  formValues: PropTypes.shape({
    header_image_id: PropTypes.string,
    header_image_url: PropTypes.string,
    type: PropTypes.string,
  }),
  onUpload: PropTypes.func,
  filePreview: PropTypes.shape({
    uuid: PropTypes.string,
    cloudfront_url: PropTypes.string,
  }),
  headerImgUrl: PropTypes.string,
  headerImgId: PropTypes.string,
  routes: PropTypes.arrayOf(PropTypes.shape({})),
  router: PropTypes.shape({
    setRouteLeaveHook: PropTypes.func,
  }),
};

const getFormIdentifier = (pageType) => {
  return (pageType && `${pageType.toLowerCase()}EditPageForm` || 'EditPageForm');
}

const mapStateToProps = (state, ownProps) => {
  const { page } = ownProps;
  const pageType = page && page.type;
  const formIdentifier = getFormIdentifier(pageType);
  const formSelector = formValueSelector(formIdentifier);

  return ({
    form: getFormIdentifier(pageType),
    initialValues: page,
    formValues: formSelector(state, 'header_image_id', 'header_image_url', 'type', 'hidden'),
    filePreview: state.website.entities.filePreview[`header_image_${pageType}`] || {},
    headerImgUrl: page.header_image_url,
    headerImgId: page.header_image_id,
    isFormDirty: formIdentifier ? isDirty(formIdentifier)(state) : undefined,
  })
}

const mapDispatchToProps = (dispatch, ownProps) => {
  const { page } = ownProps;
  const pageType = page && page.type;
  const formIdentifier = getFormIdentifier(pageType);

  return ({
    onSubmit: pageView => dispatch(updatePage(pageView)),
    onUpdatePageSilently: (pageView) => dispatch(updatePageSilently(pageView)),
    onUpload: file => dispatch(uploadPhoto(file, `header_image_${pageType}`)),
    updateFormValues: (image) => {
      const imageUrl = image ? image.cloudfront_url : '';
      const imageUuid = image ? image.uuid : '';
      dispatch(change(formIdentifier, 'header_image_id', imageUuid))
      dispatch(change(formIdentifier, 'header_image_url', imageUrl))
    },
    updateFormValuesHidden: (hidden) => {
      dispatch(change(formIdentifier, 'hidden', hidden))
    },
  })
}

export default compose(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
  reduxForm({
    enableReinitialize: false,
  }),
)(EditPageFormWithRouter);



// WEBPACK FOOTER //
// ./src/components/manage/website/entities/EditPageCommon/EditPageForm.jsx